// src/store/disciplinaryIncidentSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice";

const initialState = {
  incidents: [],
  loading: false,
  error: null,
  formData: {
    termId: "",
    batchId: "",
    sectionId: "",
    studentID: "",
    problemTypeID: "",
    incidentDate: "",
    description: "",
    location: "",
    witnesses: "",
    status: "",
    isUrgent: false,
  },
  editingId: null,
  modalVisible: false,
};

// Fetch All
export const fetchDisciplinaryIncidents = createAsyncThunk(
  "disciplinaryIncidents/fetchAll",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncident/fetch/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch incidents");
      return await res.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

export const fetchDisciplinaryIncidentsByParent = createAsyncThunk(
  "disciplinaryIncidents/fetchDisciplinaryIncidentsByParent",
  async (accId, { rejectWithValue, dispatch }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncident/fetch/parent/${accId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch incidents");
      return await res.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

export const fetchActiveDisciplinaryIncidentsByStudent = createAsyncThunk(
  "disciplinaryIncidents/fetchActiveByStudent",
  async (studentID, { rejectWithValue, dispatch }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncident/fetchActive/student/${studentID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch active incidents");
      return await res.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

export const fetchDisciplinaryIncidentsByStudent = createAsyncThunk(
  "disciplinaryIncidents/fetchDisciplinaryIncidentsByStudent",
  async (accId, { rejectWithValue, dispatch }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncident/fetch/student/${accId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch incidents");
      return await res.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);
// Create

export const createDisciplinaryIncident = createAsyncThunk(
  "disciplinaryIncidents/create",
  async (incidentData, { rejectWithValue, dispatch }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncident/create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(incidentData),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to create incident");
      }
      return await res.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// Edit
export const editDisciplinaryIncident = createAsyncThunk(
  "disciplinaryIncidents/edit",
  async ({ incidentID, data }, { rejectWithValue, dispatch }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const payload = { incidentID, ...data };
      const res = await fetch(`${API_BASE_URL}/api/DisciplinaryIncident/edit`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to update incident");
      }
      return await res.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// Delete
export const deleteDisciplinaryIncident = createAsyncThunk(
  "disciplinaryIncidents/delete",
  async (incidentID, { rejectWithValue, dispatch }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncident/hardDelete/${incidentID}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed to delete incident");
      return incidentID;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

const disciplinaryIncidentSlice = createSlice({
  name: "disciplinaryIncidents",
  initialState,
  reducers: {
    setFormData(state, action) {
      state.formData = action.payload;
    },
    resetFormData(state) {
      state.formData = {
        termId: "",
        batchId: "",
        sectionId: "",
        studentID: "",
        problemTypeID: "",
        incidentDate: "",
        description: "",
        location: "",
        witnesses: "",
        status: "",
        isUrgent: false,
      };
      state.editingId = null;
      state.modalVisible = false;
    },
    setEditingIncident(state, action) {
      const inc = action.payload;
      state.editingId = inc.incidentID;
      state.formData = {
        termId: inc.termId || "",
        batchId: inc.batchId || "",
        sectionId: inc.sectionId || "",
        studentID: inc.studentID,
        problemTypeID: inc.problemTypeID,
        incidentDate: inc.incidentDate,
        description: inc.description,
        location: inc.location,
        witnesses: inc.witnesses,
        status: inc.status,
        isUrgent: inc.isUrgent,
      };
    },
    setModalVisible(state, action) {
      state.modalVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDisciplinaryIncidents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDisciplinaryIncidents.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.incidents = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchDisciplinaryIncidents.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchActiveDisciplinaryIncidentsByStudent.pending, (state) => {
  state.loading = true;
  state.error = null;
})
.addCase(
  fetchActiveDisciplinaryIncidentsByStudent.fulfilled,
  (state, { payload }) => {
    state.loading = false;
    state.incidents = Array.isArray(payload) ? payload : [];
  }
)
.addCase(
  fetchActiveDisciplinaryIncidentsByStudent.rejected,
  (state, { payload }) => {
    state.loading = false;
    state.error = payload;
  }
)
      //
      .addCase(fetchDisciplinaryIncidentsByParent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDisciplinaryIncidentsByParent.fulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.incidents = Array.isArray(payload) ? payload : [];
        }
      )
      .addCase(
        fetchDisciplinaryIncidentsByParent.rejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload;
        }
      )
      //fetchDisciplinaryIncidentsByParent
      //fetchDisciplinaryIncidentsByStudent
      .addCase(fetchDisciplinaryIncidentsByStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDisciplinaryIncidentsByStudent.fulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.incidents = Array.isArray(payload) ? payload : [];
        }
      )
      .addCase(
        fetchDisciplinaryIncidentsByStudent.rejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload;
        }
      )
      .addCase(createDisciplinaryIncident.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDisciplinaryIncident.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.incidents.push(payload);
      })
      .addCase(createDisciplinaryIncident.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(editDisciplinaryIncident.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editDisciplinaryIncident.fulfilled, (state, { payload }) => {
        state.loading = false;
        const idx = state.incidents.findIndex(
          (i) => i.incidentID === payload.incidentID
        );
        if (idx !== -1) state.incidents[idx] = payload;
      })
      .addCase(editDisciplinaryIncident.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(deleteDisciplinaryIncident.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDisciplinaryIncident.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.incidents = state.incidents.filter(
          (i) => i.incidentID !== payload
        );
      })
      .addCase(deleteDisciplinaryIncident.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

export const {
  setFormData,
  resetFormData,
  setEditingIncident,
  setModalVisible,
} = disciplinaryIncidentSlice.actions;
export default disciplinaryIncidentSlice.reducer;
